import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import clsx from 'clsx';
import {
  Bold,
  Code,
  Highlighter,
  Italic,
  Link2,
  Link2Off,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Table as TableIcon,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { JSONContent } from '@tiptap/react';
import type { RichDoc } from '@imsenta/shared';
import { CALLOUT_KINDS, Callout, CrossReference } from './extensions';

export const RICH_TEXT_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [3, 4, 5] },
    codeBlock: { HTMLAttributes: { class: 'pf-code' } },
  }),
  Underline,
  Highlight,
  Link.configure({
    openOnClick: false,
    // Off deliberately: many ordinary words after a full stop are live TLDs, so
    // "Contact ops.team about it" would silently become a hyperlink and carry
    // into the exported Markdown and PDF. The toolbar has an explicit control.
    autolink: false,
    protocols: ['http', 'https', 'mailto'],
    HTMLAttributes: { rel: 'noopener noreferrer nofollow' },
  }),
  Table.configure({ resizable: true, allowTableNodeSelection: true }),
  TableRow,
  TableHeader,
  TableCell,
  TaskList,
  TaskItem.configure({ nested: true }),
  CrossReference,
  Callout,
];

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: ReactNode;
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={clsx(
        'inline-flex size-7 items-center justify-center rounded transition-colors',
        active ? 'bg-brand-100 text-brand-700' : 'text-ink-500 hover:bg-ink-100 hover:text-ink-800',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-ink-200" aria-hidden />;
}

export interface RichTextEditorProps {
  value: RichDoc;
  onChange: (value: RichDoc) => void;
  placeholder?: string;
  editable?: boolean;
  /** Renders extra toolbar controls, e.g. the cross-reference picker. */
  toolbarExtras?: (editor: Editor) => ReactNode;
  onFocus?: () => void;
  className?: string;
  minHeight?: number;
}

/**
 * The authoring surface.
 *
 * Content is held as ProseMirror JSON — the same shape the API stores — so the
 * author never sees or types Markdown; the serialiser produces it on export.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write the content of this section…',
  editable = true,
  toolbarExtras,
  onFocus,
  className,
  minHeight = 90,
}: RichTextEditorProps) {
  // Guards against feeding our own change back in as an external update.
  const emitting = useRef(false);

  const editor = useEditor({
    extensions: [...RICH_TEXT_EXTENSIONS, Placeholder.configure({ placeholder })],
    content: value as unknown as JSONContent,
    editable,
    editorProps: { attributes: { class: 'focus:outline-none', style: `min-height:${minHeight}px` } },
    onUpdate: ({ editor: instance }) => {
      emitting.current = true;
      onChange(instance.getJSON() as RichDoc);
      // Cleared on the next tick, after React has processed the state update.
      queueMicrotask(() => {
        emitting.current = false;
      });
    },
    onFocus,
  });

  useEffect(() => {
    if (!editor || emitting.current) return;
    const current = JSON.stringify(editor.getJSON());
    if (current === JSON.stringify(value)) return;
    editor.commands.setContent(value as unknown as JSONContent, false);
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = String(editor.getAttributes('link').href ?? '');
    const href = window.prompt('Link address (https://… or mailto:…)', previous);
    if (href === null) return;
    if (href === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    if (!/^(https?:|mailto:)/i.test(href)) {
      window.alert('Links must start with https://, http:// or mailto:');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  }, [editor]);

  if (!editor) return <div className="h-24 animate-pulse rounded-lg bg-ink-100" />;

  return (
    <div className={clsx('pf-editor rounded-lg border border-ink-200 bg-white focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-100', className)}>
      {editable ? (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-ink-200 bg-ink-50/70 px-1.5 py-1">
          <ToolbarButton title="Bold (Ctrl+B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Italic (Ctrl+I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <UnderlineIcon className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
            <Strikethrough className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Highlight" active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()}>
            <Highlighter className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Inline code" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
            <Code className="size-3.5" />
          </ToolbarButton>

          <Divider />

          <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <List className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <ListOrdered className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Checklist" active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}>
            <ListTodo className="size-3.5" />
          </ToolbarButton>

          <Divider />

          <ToolbarButton title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
            <Quote className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            title="Insert table"
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          >
            <TableIcon className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton title="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
            <Minus className="size-3.5" />
          </ToolbarButton>

          <Divider />

          <ToolbarButton title={editor.isActive('link') ? 'Edit link' : 'Add link'} active={editor.isActive('link')} onClick={setLink}>
            <Link2 className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            title="Remove link"
            disabled={!editor.isActive('link')}
            onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
          >
            <Link2Off className="size-3.5" />
          </ToolbarButton>

          <Divider />

          <select
            value={CALLOUT_KINDS.find((c) => editor.isActive('callout', { kind: c.kind }))?.kind ?? ''}
            onChange={(e) => {
              const kind = e.target.value;
              if (!kind) editor.chain().focus().lift('callout').run();
              else editor.chain().focus().toggleCallout(kind).run();
            }}
            className="h-7 rounded border border-ink-200 bg-white px-1.5 text-[12px] text-ink-600 outline-none hover:border-ink-300"
            title="Callout box"
          >
            <option value="">Callout…</option>
            {CALLOUT_KINDS.map((c) => (
              <option key={c.kind} value={c.kind}>
                {c.label}
              </option>
            ))}
          </select>

          {editor.isActive('table') ? (
            <>
              <Divider />
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().addRowAfter().run()}
                className="rounded px-1.5 py-1 text-[11.5px] text-ink-600 hover:bg-ink-100"
              >
                +Row
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                className="rounded px-1.5 py-1 text-[11.5px] text-ink-600 hover:bg-ink-100"
              >
                +Col
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().deleteRow().run()}
                className="rounded px-1.5 py-1 text-[11.5px] text-rose-600 hover:bg-rose-50"
              >
                −Row
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().deleteColumn().run()}
                className="rounded px-1.5 py-1 text-[11.5px] text-rose-600 hover:bg-rose-50"
              >
                −Col
              </button>
            </>
          ) : null}

          {toolbarExtras ? (
            <>
              <Divider />
              {toolbarExtras(editor)}
            </>
          ) : null}

          <div className="ml-auto flex items-center gap-0.5">
            <ToolbarButton title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
              <Undo2 className="size-3.5" />
            </ToolbarButton>
            <ToolbarButton title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
              <Redo2 className="size-3.5" />
            </ToolbarButton>
          </div>
        </div>
      ) : null}

      <EditorContent editor={editor} className="px-3.5 py-3" />
    </div>
  );
}
